import { useMemo } from "react";
import { notesListParams } from "@/features/notes/lib/list-query-params";
import type {
	NotesAdapter,
	NotesCreateVars,
	NotesDeleteVars,
	NotesUpdateVars,
} from "@/features/notes/types";
import {
	useCreateJobNote,
	useDeleteJobNote,
	useUpdateJobNote,
} from "@/services/job/job.mutations";
import { jobQueryOptions } from "@/services/job/job.options";

/**
 * Pure list query options for job notes (route loaders).
 */
export function createJobNotesListQueryOptions(args: {
	jobId: string;
	organizationId: string;
	start: number;
	count: number;
}) {
	return jobQueryOptions.notes.list(
		args.jobId,
		args.start,
		args.count,
		notesListParams(args.organizationId),
		{ organizationId: args.organizationId },
	);
}

/**
 * Job-scoped notes adapter (hooks + query option factories).
 */
export function useJobNotesAdapter(jobId: string): NotesAdapter {
	const createNote = useCreateJobNote();
	const updateNote = useUpdateJobNote();
	const deleteNote = useDeleteJobNote();

	return useMemo(
		(): NotesAdapter => ({
			kind: "job",
			entityId: jobId,
			listQueryOptions: ({ organizationId, start, count }) =>
				createJobNotesListQueryOptions({
					jobId,
					organizationId,
					start,
					count,
				}),
			createMutation: {
				mutateAsync: (vars: NotesCreateVars) =>
					createNote.mutateAsync({
						jobId: vars.entityId,
						text: vars.text,
						organizationId: vars.organizationId,
					}),
				reset: () => createNote.reset(),
				isPending: createNote.isPending,
				error: (createNote.error as Error | null) ?? null,
			},
			updateMutation: {
				mutateAsync: (vars: NotesUpdateVars) =>
					updateNote.mutateAsync({
						jobId: vars.entityId,
						noteId: vars.noteId,
						text: vars.text,
						organizationId: vars.organizationId,
					}),
				reset: () => updateNote.reset(),
				isPending: updateNote.isPending,
				error: (updateNote.error as Error | null) ?? null,
			},
			deleteMutation: {
				mutateAsync: (vars: NotesDeleteVars) =>
					deleteNote.mutateAsync({
						jobId: vars.entityId,
						noteId: vars.noteId,
						organizationId: vars.organizationId,
					}),
				reset: () => deleteNote.reset(),
				isPending: deleteNote.isPending,
				error: (deleteNote.error as Error | null) ?? null,
			},
		}),
		[createNote, deleteNote, jobId, updateNote],
	);
}
